import React, { memo } from 'react'
import { Card, Progress, Spin, Statistic } from 'antd'
import { DollarOutlined, ThunderboltOutlined, ShoppingCartOutlined, WalletOutlined } from '@ant-design/icons'
import './SuperadminCostSummary.scss'

const fmt = (num, decimals = 2) => {
  if (num === null || num === undefined) return '—'
  const n = parseFloat(num)
  if (isNaN(n)) return '—'
  return n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

const fmtCredits = (num) => {
  if (num === null || num === undefined) return '—'
  return Math.round(parseFloat(num) || 0).toLocaleString()
}

const SuperadminCostSummary = memo(({ data, loading }) => {
  if (!data && !loading) return null

  const {
    totalCredits = 0,
    totalUsed = 0,
    remainingCredits = 0,
    consumedPercent = 0,
    creditValue = 0,
    purchasedCredits = 0,
    totalCost = 0
  } = data || {}
  const displayPercent = typeof consumedPercent === 'number'
    ? (Number.isInteger(consumedPercent) ? consumedPercent : parseFloat(consumedPercent.toFixed(1)))
    : 0

  const metrics = [
    {
      key: 'purchased',
      icon: <ShoppingCartOutlined />,
      label: 'Credits Purchased',
      value: fmtCredits(purchasedCredits || totalCredits),
      accent: '#5B6EE2',
      bg: '#f0f1fd'
    },
    {
      key: 'cost',
      icon: <DollarOutlined />,
      label: 'Total Cost',
      value: `$${fmt(totalCost)}`,
      sub: creditValue ? `@ $${creditValue} / credit` : null,
      accent: '#44959A',
      bg: '#edf7f7'
    },
    {
      key: 'used',
      icon: <ThunderboltOutlined />,
      label: 'Total Usage',
      value: fmtCredits(totalUsed) + ' credits',
      accent: '#EB3B88',
      bg: '#fef0f6'
    },
    {
      key: 'remaining',
      icon: <WalletOutlined />,
      label: 'Remaining Credits',
      value: fmtCredits(remainingCredits),
      accent: '#3875F6',
      bg: '#eef3ff'
    }
  ]

  return (
    <Card className="superadmin-cost-summary-card">
      <Spin spinning={loading}>
        <div className="scs-inner">
          <div className="scs-metrics">
            {metrics.map(m => (
              <div key={m.key} className="scs-metric-card" style={{ '--accent': m.accent, '--bg': m.bg }}>
                <div className="scs-metric-icon">{m.icon}</div>
                <div className="scs-metric-body">
                  <span className="scs-metric-value">{m.value}</span>
                  <span className="scs-metric-label">{m.label}</span>
                  {m.sub && <span className="scs-metric-sub">{m.sub}</span>}
                </div>
              </div>
            ))}
          </div>

          <div className="scs-progress-section">
            <div className="scs-progress-header">
              <span className="scs-progress-label">Credits Consumed</span>
              <span className="scs-progress-values">
                {fmtCredits(totalUsed)} of {fmtCredits(totalCredits)}
              </span>
            </div>
            <div className='scs-progress-bar-container'>
              <Progress
                percent={displayPercent}
                strokeColor={{
                  '0%': '#5B6EE2',
                  '100%': '#44959A'
                }}
                trailColor="#f0f0f0"
                showInfo={true}
                format={() => `${displayPercent}%`}
                className="scs-progress-bar"
                strokeWidth={10}
              />
            </div>

          </div>
        </div>
      </Spin>
    </Card>
  )
})

SuperadminCostSummary.displayName = 'SuperadminCostSummary'
export default SuperadminCostSummary
